import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Check, Circle, TrendingUp, TrendingDown } from "lucide-react";
import { RecurringSeries } from "@/hooks/use-recurring-transactions";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { cn } from "@/lib/utils";

interface RecurringSeriesModalProps {
  series: RecurringSeries | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onMarkAsPaid: (transactionId: string) => void;
}

export const RecurringSeriesModal = ({
  series,
  open,
  onOpenChange,
  onMarkAsPaid,
}: RecurringSeriesModalProps) => {
  if (!series) return null;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const totalPaid = series.transactions
    .filter(t => t.is_paid)
    .reduce((sum, t) => sum + t.amount, 0);

  const totalPending = series.transactions
    .filter(t => !t.is_paid)
    .reduce((sum, t) => sum + t.amount, 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {series.type === "income" ? (
              <TrendingUp className="h-5 w-5 text-success" />
            ) : (
              <TrendingDown className="h-5 w-5 text-destructive" />
            )}
            {series.description}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Summary */}
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 rounded-lg bg-success/10">
              <p className="text-xs text-muted-foreground">Pago</p>
              <p className="text-lg font-bold text-success">{formatCurrency(totalPaid)}</p>
              <p className="text-xs text-muted-foreground">
                {series.paidInstallments} de {series.totalInstallments || "∞"}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-destructive/10">
              <p className="text-xs text-muted-foreground">Pendente</p>
              <p className="text-lg font-bold text-destructive">{formatCurrency(totalPending)}</p>
              <p className="text-xs text-muted-foreground">
                {series.transactions.filter(t => !t.is_paid).length} restantes
              </p>
            </div>
          </div>

          {/* Timeline */}
          <div>
            <h4 className="text-sm font-medium mb-3">Linha do Tempo</h4>
            <ScrollArea className="h-[300px] pr-4">
              <div className="relative">
                {/* Timeline line */}
                <div className="absolute left-4 top-0 bottom-0 w-px bg-border" />

                <div className="space-y-3">
                  {series.transactions.map((tx, index) => (
                    <div
                      key={tx.id}
                      className={cn(
                        "relative pl-10 pr-2 py-2 rounded-lg transition-colors",
                        tx.is_paid ? "bg-success/5" : "bg-muted/30 hover:bg-muted/50"
                      )}
                    >
                      {/* Timeline dot */}
                      <div
                        className={cn(
                          "absolute left-2 top-1/2 -translate-y-1/2 h-5 w-5 rounded-full flex items-center justify-center",
                          tx.is_paid 
                            ? "bg-success text-success-foreground" 
                            : "bg-muted border-2 border-border"
                        )}
                      >
                        {tx.is_paid ? (
                          <Check className="h-3 w-3" />
                        ) : (
                          <Circle className="h-2 w-2" />
                        )}
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">
                              {tx.installment_number && tx.total_installments 
                                ? `Parcela ${tx.installment_number}/${tx.total_installments}`
                                : `#${index + 1}`
                              }
                            </span>
                            {tx.is_paid && (
                              <Badge variant="secondary" className="text-xs">
                                Pago
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {format(new Date(tx.transaction_date), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                          </p>
                        </div>

                        <div className="flex items-center gap-2">
                          <span className={cn(
                            "font-medium",
                            tx.is_paid ? "text-muted-foreground" : "text-foreground"
                          )}>
                            {formatCurrency(tx.amount)}
                          </span>
                          {!tx.is_paid && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => onMarkAsPaid(tx.id)}
                            >
                              <Check className="h-3 w-3 mr-1" />
                              Pagar
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </ScrollArea>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
